import * as turf from '@turf/turf';
import { getFeatures, cqlString, LAYERS } from './wfsClient';
import { isPointInFeature } from './mapHelpers';

/* Punto garantizado dentro de la geometría (sirve igual para puntos y polígonos) */
function innerPoint(feature) {
    const [lng, lat] = turf.pointOnFeature(feature).geometry.coordinates;
    return { lat, lng };
}

/* Parcela de un comercio: primero por id_inmueble, si no la parcela que lo contiene */
export async function findMatchingParcel(commerceFeature, signal) {
    if (!commerceFeature) return null;

    const idInmueble = commerceFeature.properties && commerceFeature.properties.id_inmueble;
    if (idInmueble) {
        const byId = await getFeatures(LAYERS.parcelas, { cql: `id_contribuyente = ${cqlString(idInmueble)}`, maxFeatures: 1, signal });
        if (byId.length > 0) return byId[0];
    }

    if (!commerceFeature.geometry) return null;
    const { lat, lng } = innerPoint(commerceFeature);
    const buffer = 0.0002;
    const candidates = await getFeatures(LAYERS.parcelas, { bbox: [lng - buffer, lat - buffer, lng + buffer, lat + buffer], maxFeatures: 150, signal });
    const containing = candidates.filter(p => isPointInFeature(lat, lng, p));
    if (containing.length <= 1) return containing[0] || null;

    // Parcelas superpuestas (codominios): nos quedamos con la de área más parecida al comercio
    const commerceArea = turf.area(commerceFeature);
    const areaDiff = (p) => Math.abs(turf.area(p) - commerceArea);
    return containing.reduce((best, p) => (areaDiff(p) < areaDiff(best) ? p : best));
}

/* Comercios de una parcela: por id_inmueble y, si no hay y no es estricto, los ubicados dentro de la parcela */
export async function fetchRelatedCommerces(parcelFeature, { strict = false, signal } = {}) {
    if (!parcelFeature) return [];

    const pId = parcelFeature.properties && parcelFeature.properties.id_contribuyente;
    if (pId) {
        const byId = await getFeatures(LAYERS.comercios, { cql: `id_inmueble = ${cqlString(pId)}`, maxFeatures: 50, signal });
        if (byId.length > 0) return byId;
    }

    if (strict || !parcelFeature.geometry) return [];

    const [minX, minY, maxX, maxY] = turf.bbox(parcelFeature);
    const buffer = 0.0001;
    const candidates = await getFeatures(LAYERS.comercios, { bbox: [minX - buffer, minY - buffer, maxX + buffer, maxY + buffer], maxFeatures: 100, signal });
    return candidates.filter(c => {
        if (!c.geometry) return false;
        const { lat, lng } = innerPoint(c);
        return isPointInFeature(lat, lng, parcelFeature);
    });
}

/* Parcelas que comparten los primeros 10 caracteres de la nomenclatura (subdivisiones y codominios) */
export async function fetchChildParcels(parcelFeature, signal) {
    const props = (parcelFeature && parcelFeature.properties) || {};
    const field = Object.keys(props).find(k => k.toLowerCase().includes('nomen')) || 'Nomenclatura';
    const nom = String(props[field] || '').trim();
    if (!nom) return [];
    return getFeatures(LAYERS.parcelas, { cql: `${field} LIKE ${cqlString(nom.slice(0, 10) + '%')}`, maxFeatures: 2000, signal });
}
